<template>
  <v-container fluid fill-height v-resize="onResize">
    <div ref="container" style="position: absolute; top: 0px; right: 0px; bottom: 0px; left: 0px;"></div>
    <v-progress-linear
      absolute top
      color="red lighten-2"
      v-model="progressValue"
      v-if="progressValue > 0"
    ></v-progress-linear>
  </v-container>
</template>

<script>
/* eslint-disable no-unused-vars */
import { mapState } from 'vuex'

const THREE = window.THREE

function formatURL (tmpl, v, u) {
  const slug = '/build/'
  return slug + tmpl
    .replace('%v', v)
    .replace('%u', u)
}

function getImagesList (conf, tour, level) {
  return conf[tour]?.image.level.filter(e => e.index === level)[0]
}

export default {
  name: 'Tour',

  data: () => ({
    progressValue: 0,

    renderer: false,
    camera: false,
    scene: false,
    container: false,
    skyBox: false,
    controls: false,
    hotspotsGroup: false,
    selectedObject: null,
    raycaster: new THREE.Raycaster(),
    pointer: new THREE.Vector2(),
    skyBoxMaterials: []
  }),

  computed: mapState(['conf']),

  watch: {
    '$route.params.id': {
      handler () {
        this.loadScene(this.conf, this.$route.params.id)
      }
    }
  },

  methods: {
    onResize () {
      if (this.renderer && this.camera) {
        this.camera.aspect = this.container.clientWidth / this.container.clientHeight
        this.camera.updateProjectionMatrix()
        this.renderer.setSize(this.container.clientWidth, this.container.clientHeight)
      }
    },

    createControls (camera, renderer, theta) {
      if (this.controls?.dispose) {
        this.controls.dispose()
        this.controls.reset()
      }
      this.controls = new THREE.OrbitControls(camera, renderer.domElement, theta)
      this.controls.enableZoom = false
      this.controls.enablePan = false
      this.controls.enableDamping = true
      this.controls.rotateSpeed = -0.25
    },

    createHotSpots (hotspots) {
      const textureLoader = new THREE.TextureLoader()
      const mapB = textureLoader.load('sprite1.png')
      const mapC = textureLoader.load('sprite2.png')

      const group = new THREE.Group()

      const materialC = new THREE.SpriteMaterial({ map: mapC, color: 0xffffff, fog: true })
      const materialB = new THREE.SpriteMaterial({ map: mapB, color: 0xffffff, fog: true });

      (hotspots || []).forEach(function (hotspot) {
        const material = materialC.clone()
        material.color.set('#aaa')
        material.map.offset.set(-0.5, -0.5)
        material.map.repeat.set(2, 2)

        const sprite = new THREE.Sprite(material)

        const ath = (0 + hotspot.ath * 1) * Math.PI / 180
        const atv = (90 + hotspot.atv * 1) * Math.PI / 180
        const x = 0.4 * Math.sin(atv) * Math.cos(ath)
        const z = 0.4 * Math.sin(atv) * Math.sin(ath)
        const y = 0.4 * Math.cos(atv)

        sprite.position.set(x, y, z)
        sprite.scale.set(0.1, 0.1, 1)

        sprite.userData = {
          title: hotspot.title,
          ref: hotspot.ref
        }

        group.add(sprite)
      })

      return group
    },

    loadTexture (tilesize, images, side) {
      const self = this

      const texture = new THREE.Texture()
      const canvas = document.createElement('canvas')
      // document.getElementById( 'container' ).appendChild(canvas);
      const context = canvas.getContext('2d')
      canvas.width = images.tiledimagewidth
      canvas.height = images.tiledimageheight

      let count = 0
      let progress = 0
      const tots = Math.ceil(images.tiledimagewidth / tilesize) * Math.ceil(images.tiledimageheight / tilesize)
      for (let x = 0; x < Math.ceil(images.tiledimagewidth / tilesize); x++) {
        for (let y = 0; y < Math.ceil(images.tiledimageheight / tilesize); y++) {
          count++
          const imageObj = new Image()
          imageObj.onload = function () {
            count--
            progress++

            self.progressValue = 100 * progress / tots

            context.drawImage(imageObj, tilesize * x, tilesize * y, imageObj.width, imageObj.height)
            if (count === 0) {
              if (side === 'up' || side === 'down') {
                const tmpCanvas = document.createElement('canvas')
                tmpCanvas.width = canvas.width
                tmpCanvas.height = canvas.height
                const ctx = tmpCanvas.getContext('2d')
                ctx.translate(canvas.width / 2, canvas.height / 2)
                ctx.rotate(side === 'up' ? -Math.PI / 2 : Math.PI / 2)
                ctx.drawImage(canvas, -canvas.width / 2, -canvas.height / 2)
                texture.image = tmpCanvas
              } else {
                texture.image = canvas
              }
              texture.needsUpdate = true
              self.progressValue = 0
            }
          }
          imageObj.src = formatURL(images[side].url, y, x)
        }
      }
      return texture
    },

    onPointerMove (event) {
      if (this.selectedObject) {
        this.selectedObject.material.color.set('#aaa')
        this.selectedObject = null
      }
      const box = this.container.getBoundingClientRect()
      this.pointer.x = ((event.clientX - box.left) / this.container.clientWidth) * 2 - 1
      this.pointer.y = -((event.clientY - box.top) / this.container.clientHeight) * 2 + 1

      this.raycaster.setFromCamera(this.pointer, this.camera)

      const intersects = this.raycaster.intersectObject(this.hotspotsGroup, true)

      if (intersects.length > 0) {
        const res = intersects.filter(function (res) {
          return res && res.object
        })[0]

        if (res && res.object) {
          this.selectedObject = res.object
          this.selectedObject.material.color.set('#fff')
        }
      }
    },

    onPointerClick (event) {
      let prevUserData = this.selectedObject?.userData

      if (this.selectedObject) {
        this.selectedObject.material.color.set('#aaa')
        this.selectedObject = null
      }

      const item = event?.touches?.[0] || event
      const box = this.container.getBoundingClientRect()
      this.pointer.x = ((item.clientX - box.left) / this.container.clientWidth) * 2 - 1
      this.pointer.y = -((item.clientY - box.top) / this.container.clientHeight) * 2 + 1

      this.raycaster.setFromCamera(this.pointer, this.camera)

      const intersects = this.raycaster.intersectObject(this.hotspotsGroup, true)

      if (intersects.length > 0) {
        const res = intersects.filter(function (res) {
          return res && res.object
        })[0]

        if (res && res.object) {
          this.selectedObject = res.object
          this.selectedObject.material.color.set('#fff')
          if (prevUserData) {
            if (prevUserData?.ref === this.selectedObject?.userData.ref) {
              // this.loadScene(this.conf, prevUserData.ref)
              this.$router.replace({ name: 'location', params: { id: prevUserData.ref } })
              this.selectedObject = null
              prevUserData = null
            }
          }
        }
      }
    },

    loadScene (conf, tour) {
      const scene = this.scene
      scene.remove(this.hotspotsGroup)

      const tilesize = conf[tour].image.tilesize * 1
      const hlookat = conf[tour].view.hlookat
      const images = getImagesList(conf, tour, 0) || getImagesList(conf, tour, 1)

      this.createControls(this.camera, this.renderer, (-90 + hlookat * 1) * Math.PI / 180)

      self.progressValue = 0

      const textures = [
        this.loadTexture(tilesize, images, 'front'),
        this.loadTexture(tilesize, images, 'back'),
        this.loadTexture(tilesize, images, 'up'),
        this.loadTexture(tilesize, images, 'down'),
        this.loadTexture(tilesize, images, 'left'),
        this.loadTexture(tilesize, images, 'right')
      ]

      this.skyBoxMaterials.splice(0, this.skyBoxMaterials.length)

      for (let i = 0; i < 6; i++) {
        this.skyBoxMaterials.push(new THREE.MeshBasicMaterial({ map: textures[i] }))
      }

      this.scene.add(this.hotspotsGroup = this.createHotSpots(conf[tour].hotspot))
    },

    animate () {
      requestAnimationFrame(this.animate)

      this.controls && this.controls.update() // required when damping is enabled

      if (this.hotspotsGroup) {
        for (let i = 0, l = this.hotspotsGroup.children.length; i < l; i++) {
          const sprite = this.hotspotsGroup.children[i]
          sprite.material.rotation += 0.01
          const s = 0.075 + 0.0125 * Math.cos(new Date().getTime() / 1000)
          sprite.scale.set(s, s, 1)
        }
      }

      this.renderer.render(this.scene, this.camera)
    }
  },

  mounted () {
    const self = this

    this.container = this.$refs.container

    this.renderer = new THREE.WebGLRenderer()
    this.renderer.setPixelRatio(window.devicePixelRatio)
    this.renderer.setSize(this.container.clientWidth, this.container.clientHeight)
    this.container.appendChild(this.renderer.domElement)

    this.scene = new THREE.Scene()

    this.camera = new THREE.PerspectiveCamera(80, this.container.clientWidth / this.container.clientHeight, 0.01, 100)
    this.camera.position.z = 0.01

    this.skyBox = new THREE.Mesh(new THREE.BoxGeometry(1, 1, -1), this.skyBoxMaterials)
    this.scene.add(this.skyBox)

    this.renderer.domElement.addEventListener('wheel', function (event) {
      event.preventDefault()
      console.log('wheel')
      let step = 1
      if (event.deltaY < 0) step = -1
      self.camera.fov += step
      if (self.camera.fov > 80) self.camera.fov = 80
      if (self.camera.fov < 25) self.camera.fov = 25
      self.camera.updateProjectionMatrix()
    }, false)

    this.container.addEventListener('pointermove', this.onPointerMove)
    this.container.addEventListener('touchstart', this.onPointerClick)
    this.container.addEventListener('click', this.onPointerClick)

    this.animate()
    this.loadScene(this.conf, this.$route.params.id)
  }
}
</script>
